import { useCallback, useMemo, useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { debounce } from 'lodash';
import AttachmentsItem from './AttachmentsItem';
import UploadFile from '../../../Shared/Components/UploadFile';
import { convertFileSizeToMbString } from '../../../utils/helpers';
import useGetProcessedTimesheetAttachments from '../../../Admin/hooks/useGetProcessedTimesheetAttachments';
import useDownloadProcessedTimesheetAttachment from '../../../Admin/hooks/useDownloadProcessedTimesheetAttachment';
import usePatchProcessedTimesheetAttachmentTypeAndLocation from '../../../Admin/hooks/usePatchProcessedTimesheetAttachmentTypeAndLocation';
import usePostProcessedTimesheetAttachment from '../../../Admin/hooks/usePostProcessedTimesheetAttachment';
import useDeleteProcessedTimesheetAttachment from '../../../Admin/hooks/useDeleteProcessedTimesheetAttachment';
import useToast from '../../../utils/useToast';
import { ATTACHMENTS_CONFIG } from '../../../Admin/Configurations/processedTimesheetsConfig';
import useAdminAuthContext from '../../../Admin/hooks/useAdminAuthContext';

const orderByCreateDateDesc = (a, b) => new Date(b.created) - new Date(a.created);

function Attachments({
    attachments: initialAttachments,
    processedTimesheetId,
    rawTimesheetId,
    readOnly = false,
    locations = [],
}) {
    const { isTimesheetSystemAdminUser, isTimesheetAdminUser } = useAdminAuthContext();
    const canEdit = (isTimesheetSystemAdminUser || isTimesheetAdminUser) && !readOnly;
    const { renderToast, showSuccessToast, showErrorToast } = useToast();
    const [uploadQueue, setUploadQueue] = useState([]);

    const mappedLocations = useMemo(
        () =>
            locations.map(({ locationId, locationName }) => ({
                id: locationId,
                displayName: locationName,
            })),
        [locations]
    );

    const {
        data: attachments,
        isLoading,
        refetch,
    } = useGetProcessedTimesheetAttachments(
        { processedTimesheetId },
        { initialData: initialAttachments }
    );

    const { mutate: upload } = usePostProcessedTimesheetAttachment({
        onSuccess: () => {
            refetch();
        },
        onError: (error, variables) => {
            let errorMessage = '';
            const { file } = variables;
            const { errorCode } = error;
            switch (errorCode) {
                case 403:
                    errorMessage = `You are not authorized to upload attachments`;
                    break;
                case 405:
                    errorMessage = `Failed to upload attachment. Maximum number of attachments reached (${ATTACHMENTS_CONFIG.MAX_ITEMS_COUNT}).`;
                    break;
                case 413:
                    errorMessage = `The file ${file.name} is too large. Maximum size is ${ATTACHMENTS_CONFIG.MAX_FILE_SIZE_MB}MB!`;
                    break;
                default:
                    errorMessage = `Failed to upload attachment with name: ${file.name}. Please try again.`;
            }
            showErrorToast(errorMessage);
        },
        onSettled: (data, error, variables) => {
            const { file } = variables;
            setUploadQueue((prev) => prev.filter((item) => item.file.name !== file.name));
        },
    });

    const { mutate: download } = useDownloadProcessedTimesheetAttachment({
        onError: () => showErrorToast('Failed to download attachment.'),
    });

    const { mutate: patchTypeAndLocation } = usePatchProcessedTimesheetAttachmentTypeAndLocation({
        onSuccess: (_, variables) =>
            showSuccessToast(
                `Attachment ${variables?.locationId ? 'location' : 'type'} updated successfully`
            ),
        onError: (_, variables) =>
            showErrorToast(
                `Error setting attachment ${
                    variables?.locationId ? 'location' : 'type'
                }. Please try again.`
            ),
    });

    const { mutate: deleteAttachment } = useDeleteProcessedTimesheetAttachment({
        onSuccess: () => {
            refetch();
        },
        onError: (error, variables) => {
            showErrorToast(
                `Failed to delete attachment with name: ${variables.fileName}. Please try again.`
            );
        },
    });

    const handleUploadFiles = (files) => {
        // Add files to upload queue
        const newUploads = files.map((file) => ({
            file,
            progress: 0,
            controller: new AbortController(),
        }));

        setUploadQueue((prev) => [...prev, ...newUploads]);

        // Start uploads
        newUploads.forEach((uploadItem) => {
            const { file, controller } = uploadItem;

            // Create progress callback debounced for this specific file
            const onUploadProgress = debounce((progressEvent) => {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                setUploadQueue((prev) =>
                    prev.map((item) =>
                        item.file.name === file.name
                            ? { ...item, progress: percentCompleted }
                            : item
                    )
                );
            }, 10);

            // Upload the file with progress tracking and abort signal
            upload({
                processedTimesheetId,
                rawTimesheetId,
                file,
                onUploadProgress,
                signal: controller.signal,
            });
        });
    };

    const handleCancelUpload = (fileName) => {
        // Find and abort the upload
        const uploadItem = uploadQueue.find((item) => item.file.name === fileName);
        if (uploadItem?.controller) uploadItem.controller.abort();
        // Remove from upload queue
        setUploadQueue((prev) => prev.filter((item) => item.file.name !== fileName));
    };

    const ltoeAttachments = useMemo(
        () =>
            attachments?.filter((item) => item.source === 'LTOE').sort(orderByCreateDateDesc) || [],
        [attachments]
    );
    const bookingAttachments = useMemo(
        () =>
            attachments?.filter((item) => item.source === 'Booking').sort(orderByCreateDateDesc) ||
            [],
        [attachments]
    );
    const renderAttachmentItem = useCallback(
        (item, source) => (
            <AttachmentsItem
                key={item.id}
                id={item.id}
                name={item.name}
                type={item.type}
                size={item.size}
                url={item.url}
                isReadOnly={!canEdit}
                locationId={item.locationId}
                documentType={item.documentType}
                locations={mappedLocations}
                isDelible={item.isDelible}
                source={source}
                onDownload={(options) =>
                    download(
                        { attachmentId: item.id, source: item.source, fileName: item.name },
                        options
                    )
                }
                onChangeLocation={({ locationId }, options) =>
                    patchTypeAndLocation({ id: item.id, locationId }, options)
                }
                onChangeDocumentType={({ documentType }, options) =>
                    patchTypeAndLocation({ id: item.id, documentType }, options)
                }
                onDelete={(options) =>
                    deleteAttachment(
                        { fileName: item.name, processedTimesheetId, rawTimesheetId },
                        options
                    )
                }
            />
        ),
        [canEdit, mappedLocations, download, patchTypeAndLocation, deleteAttachment]
    );

    const hasFiles = attachments?.length > 0 || uploadQueue.length > 0;

    if (isLoading) return <CircularProgress />;

    return (
        <Stack direction="column" gap={2}>
            {renderToast()}
            <Typography>Attachments ({attachments?.length || 0})</Typography>
            {canEdit && (
                <UploadFile
                    maxSizeInMB={ATTACHMENTS_CONFIG.MAX_FILE_SIZE_MB}
                    fileTypes={ATTACHMENTS_CONFIG.FILE_TYPES}
                    fileTypesLabels={ATTACHMENTS_CONFIG.FILE_TYPES_LABELS}
                    onAcceptedFiles={handleUploadFiles}
                />
            )}
            {hasFiles && (
                <>
                    {(ltoeAttachments.length > 0 || uploadQueue.length > 0) && (
                        <Stack direction="column" gap={1}>
                            <Typography fontSize="14px" fontWeight="400">
                                Manual Upload
                            </Typography>
                            {uploadQueue.map((uploadItem) => (
                                <AttachmentsItem
                                    key={uploadItem.file.name}
                                    id="upload"
                                    name={uploadItem.file.name}
                                    type={uploadItem.file.type.split('/')[1]}
                                    size={convertFileSizeToMbString(uploadItem.file.size)}
                                    isUploading
                                    uploadProgress={uploadItem.progress}
                                    onCancelUpload={() => handleCancelUpload(uploadItem.file.name)}
                                />
                            ))}
                            {ltoeAttachments.map((item) => renderAttachmentItem(item, 'LTOE'))}
                        </Stack>
                    )}
                    {bookingAttachments.length > 0 && (
                        <Stack direction="column" gap={1}>
                            <Typography fontSize="14px" fontWeight="400">
                                From the Booking
                            </Typography>
                            {bookingAttachments.map((item) =>
                                renderAttachmentItem(item, 'Booking')
                            )}
                        </Stack>
                    )}
                </>
            )}
            {!hasFiles && (
                <Typography fontSize="14px" fontWeight="400">
                    No documents attached
                </Typography>
            )}
        </Stack>
    );
}

export default Attachments;
